import React from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Card, Form, Input, Modal, Row, Select, Spin} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';

import {
    useAddContactMethodMutation,
    useDeleteContactMethodMutation,
    useGetContactMethodsQuery,
    useUpdateContactMethodMutation
} from '../../store/api/contactMethodsApi';
import {messageType, showMessages} from '../../helpers/message';
import {BaseError} from '../../types/apiTypes';
import {ContactData, ContactMethod, PhoneNumber} from '../../types/contactMethodTypes';
import {formStandaloneLayout} from '../../assets/styles';
import ErrorBoundary from '../ErrorBoundary';
import {useAppSelector} from '../../hooks/useStore';
import {selectCurrentProfile} from '../../store/slices/userSlice';

import './PhoneNumbersCard.less';

import {nothingToUpdate} from '../../helpers/text';

const {Option} = Select;

const PhoneNumbersCard = () => {
    const navigate = useNavigate();
    const profile = useAppSelector(selectCurrentProfile);
    const profileID = profile?.id;

    const [form] = Form.useForm();
    const [phoneNumbers, setPhoneNumbers] = React.useState<PhoneNumber[]>([]);
    const [isNewNum, setIsNewNum] = React.useState(false);
    const [cursorPos, setCursorPos] = React.useState<number | null>(null);
    const [addContactMethod, {
        isSuccess: addSuccess,
        isError: addError,
        isLoading: addLoading,
        error: addErrorData,
        data: addData,
        reset: resetAddMutation
    }] = useAddContactMethodMutation();
    const [deleteContactMethod, {
        isSuccess: deleteSuccess,
        isError: deleteError,
        isLoading: deleteLoading,
        error: deleteErrorData,
        data: deleteData,
        reset: resetDeleteMutation
    }] = useDeleteContactMethodMutation();
    const [updateContactMethod, {
        isSuccess: updateSuccess,
        isError: updateError,
        isLoading: updateLoading,
        error: updateErrorData,
        data: updateData,
        reset: resetUpdateMutation
    }] = useUpdateContactMethodMutation();

    const {data, isLoading: contactMethodsLoading} = useGetContactMethodsQuery({id: profileID}, {refetchOnMountOrArgChange: true});

    const phoneLoading = contactMethodsLoading || addLoading || updateLoading || deleteLoading;

    const onAdd = () => {
        const newPhoneId = Date.now();

        setPhoneNumbers((prevPhoneNumbers) => [
            ...prevPhoneNumbers,
            {type: 'cell', number: '', id: newPhoneId, error: '', isValid: true}, // Initialize error message and validity for new phone number.
        ]);

        setIsNewNum(true)
        form.resetFields();
    };

    //region add/update
    const onTypeChange = (selectedType: string, phoneNumberId: number) => {
        const updatedPhoneNumbers = phoneNumbers.map((phoneNumber) => {
            if (phoneNumber.id === phoneNumberId) {
                return {...phoneNumber, type: selectedType};
            }
            return phoneNumber;
        });

        setPhoneNumbers(updatedPhoneNumbers);
    };

    const formatPhoneNumber = (value: string, type: string) => {
        if (!value) return value;

        if (type === 'work' && value.length >= 10) {
            const phoneNumber = value.replace(/[^\d]/g, '');
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}x${phoneNumber.slice(10)}`;
        } else {
            const phoneNumber = value.replace(/[^\d]/g, '');
            const phoneNumberLength = phoneNumber.length;

            if (phoneNumberLength < 4) return phoneNumber;
            if (phoneNumberLength < 7) {
                return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
            }
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
        }
    };

    const handleBlur = (phoneNumberId: number) => {
        const updatedPhoneNumbers = phoneNumbers.map((phoneNumber) => {
            if (phoneNumber.id === phoneNumberId) {
                // Always format the phone number when the user blurs the input.
                const formattedNumber = formatPhoneNumber(phoneNumber.number, phoneNumber.type);
                return {...phoneNumber, formattedNumber};
            }
            return phoneNumber;
        });

        setPhoneNumbers(updatedPhoneNumbers);
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>, phoneNumberId: number) => {
        const {value: inputValue, selectionStart} = e.target;
        let newCursorPos = selectionStart;
        const cleanedValue = inputValue.replace(/[^\dx\s()-]/g, '');

        // Find the phone number with the specified id.
        const updatedPhoneNumbers = phoneNumbers.map((phoneNumber) => {
            if (phoneNumber.id === phoneNumberId) {
                // Format the phone number dynamically if its length exceeds 10.
                const formattedNumber =
                    cleanedValue.length > 10 ? formatPhoneNumber(cleanedValue, phoneNumber.type) : cleanedValue;

                if (selectionStart && newCursorPos && newCursorPos > 10) {
                    newCursorPos += (formattedNumber.length - cleanedValue.length);
                }

                return {...phoneNumber, number: cleanedValue, formattedNumber};
            }
            return phoneNumber;
        });

        setPhoneNumbers(updatedPhoneNumbers);
        setCursorPos(newCursorPos);
    };

    React.useEffect(() => {
        if (addSuccess && addData) {
            showMessages('Phone Number(s) successfully added.', messageType.success);
            resetAddMutation();
        } else if (updateSuccess && updateData) {
            showMessages('Phone Number(s) successfully updated.', messageType.success);
            resetUpdateMutation();
        } else if (deleteSuccess && deleteData) {
            showMessages('Phone Number successfully deleted.', messageType.success);
            resetDeleteMutation();
        } else if (addError && addErrorData) {
            showMessages((addErrorData as BaseError).data?.message, messageType.error);
        } else if (updateError && updateErrorData) {
            showMessages((updateErrorData as BaseError).data?.message, messageType.error);
        } else if (deleteError && deleteErrorData) {
            showMessages((deleteErrorData as BaseError).data?.message, messageType.error);
        }
    }, [addSuccess, addData, updateSuccess, updateData, deleteSuccess, deleteData, addError, addErrorData, updateError, updateErrorData, deleteError, deleteErrorData, resetDeleteMutation, resetAddMutation, resetUpdateMutation]);

    const handleFinish = (phone: ContactData) => {
        let isValid = true;

        const updatedPhoneNumbers = phoneNumbers.map((phoneNumber) => {
            const formattedNumber = phoneNumber.formattedNumber ?? phoneNumber.number;
            const isFormattedNumberValid = /^\d{3}-\d{3}-\d{4}(x\d{1,5})?$/.test(formattedNumber);

            if (!isFormattedNumberValid) {
                isValid = false;
                phoneNumber.error = 'Invalid phone number format.';
                phoneNumber.isValid = false;
            } else {
                phoneNumber.error = '';
                phoneNumber.isValid = true;
            }

            return phoneNumber;
        });

        setPhoneNumbers(updatedPhoneNumbers);

        if (!isValid) return;

        if (!isNewNum) {
            const phonesToUpdate: ContactData[] = [];

            updatedPhoneNumbers.forEach((updatedPhoneNumber) => {
                const originalPhoneNumber = data?.phone.find((p) => p.id === updatedPhoneNumber.id);
                if (originalPhoneNumber && (originalPhoneNumber.type[0] !== updatedPhoneNumber.type || originalPhoneNumber.value !== updatedPhoneNumber.number)) {
                    phonesToUpdate.push({
                        ...phone,
                        contactable_id: profileID,
                        contactable_type: 'profile',
                        method: ContactMethod.phone,
                        value: updatedPhoneNumber.formattedNumber || updatedPhoneNumber.number,
                        type: [updatedPhoneNumber.type],
                        id: updatedPhoneNumber.id,
                    });
                }
            });

            if (phonesToUpdate.length === 0) {
                showMessages(nothingToUpdate, messageType.error);
                return;
            }

            phonesToUpdate.forEach((contactData) => {
                updateContactMethod(contactData);
            });
        } else {
            // Filter phoneNumbers to get only those with different ids than in data.phone.
            const newPhonesToSend = updatedPhoneNumbers.filter((phoneNumber) => phoneNumber.isValid);

            const newPhoneNumbers = newPhonesToSend.filter(
                (phoneNumber) => !(data?.phone ?? []).some((existingPhone) => existingPhone.id === phoneNumber.id)
            );

            newPhoneNumbers.forEach((newPhoneNumber) => {
                // Create a ContactData object from the PhoneNumber.
                const contactData: ContactData = {
                    ...phone,
                    contactable_id: profileID,
                    contactable_type: 'profile',
                    method: ContactMethod.phone,
                    value: newPhoneNumber.formattedNumber ?? newPhoneNumber.number,
                    type: [newPhoneNumber.type],
                    id: newPhoneNumber.id
                };
                addContactMethod(contactData);
            });

            setIsNewNum(false)
        }
        // Reset form and states.
        form.resetFields();
    };
    //end region

    //region delete
    const onDeleteConfirmation = (id: number) => {
        Modal.confirm({
            title: 'Confirm Deletion',
            content: 'Are you sure you want to delete this phone number?',
            onOk() {
                if (data?.phone && data.phone.some(phoneNumber => phoneNumber.id === id)) {
                    deleteContactMethod(id).then(() => {
                        setPhoneNumbers(prevPhoneNumbers => prevPhoneNumbers.filter(phoneNumber => phoneNumber.id !== id));
                    });
                } else {
                    setPhoneNumbers(prevPhoneNumbers => prevPhoneNumbers.filter(phoneNumber => phoneNumber.id !== id));
                    showMessages('Successfully deleted.', messageType.success);
                }
            },
        });
    };
    //end region

    React.useEffect(() => {
        if (!contactMethodsLoading && data?.phone && data.phone.length > 0) {
            setPhoneNumbers((prevPhoneNumbers) => {
                return data.phone.map((phoneNumber) => ({
                    type: phoneNumber.type[0],
                    number: phoneNumber.value,
                    id: phoneNumber.id,
                    error: '',
                    isValid: true
                }));
            });
        }
    }, [contactMethodsLoading, data, addData, navigate]);

    return (
        <Card title="Phone Number" className="phone-number-card-main" bordered={true}>
            <Spin spinning={phoneLoading}>
                <ErrorBoundary>
                    <Row gutter={{sm: 16}}>
                        <Button
                            type="default"
                            block
                            className="dash-border-button"
                            icon={<PlusOutlined/>}
                            onClick={onAdd}
                        >
                            Add Phone Number
                        </Button>
                    </Row>
                </ErrorBoundary>
                <Form {...formStandaloneLayout} form={form} name="phone-number-add" onFinish={handleFinish}>
                    {phoneNumbers.map((phoneNumber) => (
                        <div className="phone-number-container" key={phoneNumber.id}>
                            <div className="input-container">
                                <Select
                                    value={phoneNumber.type}
                                    defaultValue="cell"
                                    onChange={(selectedType) => onTypeChange(selectedType, phoneNumber.id)}
                                >
                                    <Option value="cell">Cell</Option>
                                    <Option value="home">Home</Option>
                                    <Option value="work">Work</Option>
                                </Select>
                                <Input
                                    onChange={(e) => handleNumberChange(e, phoneNumber.id)}
                                    value={phoneNumber.formattedNumber ?? phoneNumber.number}
                                    placeholder="Input a number"
                                    onBlur={() => handleBlur(phoneNumber.id)}
                                    ref={(input) => {
                                        if (input && cursorPos !== null) {
                                            input.setSelectionRange(cursorPos, cursorPos);
                                        }
                                    }}
                                />
                                <DeleteOutlined
                                    className="delete-icon"
                                    onClick={() => onDeleteConfirmation(phoneNumber.id)}
                                />
                            </div>
                            {!phoneNumber.isValid && (
                                <small style={{color: 'red'}}>
                                    {phoneNumber.error}
                                </small>
                            )}
                        </div>
                    ))}
                    {
                        phoneNumbers && phoneNumbers.length > 0 && (
                            <Button type="primary" htmlType="submit">
                                Save
                            </Button>
                        )
                    }
                </Form>
            </Spin>
        </Card>
    );
};

export default PhoneNumbersCard;
